import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { catchError, EMPTY, finalize, Observable, of, tap } from 'rxjs';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { InputText } from 'primeng/inputtext';
import { Paginator } from 'primeng/paginator';
import { Select } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PreviewDataWrapperComponent } from '../../../../../../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { TableBaseAbstractComponent } from '../../../../../../../../shared/components/table-base-abstract.component';
import {
  getRolaReprezentantaOrganizacjiDropdownItems,
  ROLA_REPREZNTANTA_ORGANIZACJI_LABELS,
  RolaReprezentantaOrganizacji,
} from '../../../../../../../../shared/enums/rola-reprezentanta-organizacji.enum';
import { AdminPanelUserDetails } from '../../../../../../../../shared/models/admin-panel-user-details';
import { AdminPanelUserOrganizationRelatedPerson } from '../../../../../../../../shared/models/admin-panel-user-organization-related-person';
import { TableRequestData } from '../../../../../../../../shared/models/table/table-request-param';
import { TableResponse } from '../../../../../../../../shared/models/table-response';
import { ConstLabelReaderPipe } from '../../../../../../../../shared/pipes/const-label-reader.pipe';
import { OrganizacjaApi } from '../../../../../../../../shared/services/api/organizacja.api';
import { HttpUtils } from '../../../../../../../../shared/utils/http.utils';
import { HttpResponseService } from '../../../../../../../../core/services/http-error-response.service';
import { SpinnerService } from '../../../../../../../../core/services/spinner.service';

@Component({
  selector: 'app-admin-panel-user-organization-related-persons-table',
  imports: [
    Button,
    Card,
    ConstLabelReaderPipe,
    FormsModule,
    InputText,
    Paginator,
    PreviewDataWrapperComponent,
    Select,
    TableModule,
    TooltipModule,
  ],
  templateUrl:
    './admin-panel-user-organization-related-persons-table.component.html',
  styleUrl:
    './admin-panel-user-organization-related-persons-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelUserOrganizationRelatedPersonsTableComponent extends TableBaseAbstractComponent<AdminPanelUserOrganizationRelatedPerson> {
  @ViewChild('table') override tableRef!: Table;

  readonly #organizacjaApi = inject(OrganizacjaApi);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #spinnerService = inject(SpinnerService);
  readonly #destroyRef = inject(DestroyRef);

  userDetails = input.required<AdminPanelUserDetails>();
  organizationId = input.required<number>();

  protected tableFilters = {
    firstName: '',
    lastName: '',
    pesel: '',
    role: null as RolaReprezentantaOrganizacji | null,
  };

  protected readonly roleOptions =
    getRolaReprezentantaOrganizacjiDropdownItems();

  constructor() {
    super();
    this.tableRequest.update((value) => ({
      ...value,
      pageSize: 5,
    }));
  }

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<AdminPanelUserOrganizationRelatedPerson>> => {
    const organizationId = this.organizationId();

    if (organizationId == null) {
      return of({
        content: [],
        page: {
          size: params.pageSize,
          number: params.pageIndex,
          totalElements: 0,
          totalPages: 0,
        },
      });
    }

    return this.#organizacjaApi.getUsersRelatedToOrganizationForAdminPanel$(
      this.userDetails(),
      organizationId,
      params,
    );
  };

  protected downloadRepresentativePowerOfAttorney(
    representative: AdminPanelUserOrganizationRelatedPerson,
  ): void {
    const attachments = representative.zalaczniki ?? [];

    if (!attachments.length) {
      return;
    }

    const ids = attachments
      .map((attachment) => attachment.id)
      .filter((id): id is number => id != null);

    if (!ids.length) {
      return;
    }

    this.#spinnerService.show();

    this.#organizacjaApi
      .downloadRepresentativePowers$(ids)
      .pipe(
        tap((response) => {
          HttpUtils.downloadFile(response);
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Nie udało się pobrać pełnomocnictwa',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected clearFilters(): void {
    this.tableFilters = {
      firstName: '',
      lastName: '',
      pesel: '',
      role: null as RolaReprezentantaOrganizacji | null,
    };
    this.resetFilters();
  }

  protected mapRoleLabel(
    role: RolaReprezentantaOrganizacji | null,
  ): string {
    if (!role) {
      return '-';
    }

    return this.ROLA_REPREZNTANTA_ORGANIZACJI_LABELS[role];
  }

  protected readonly ROLA_REPREZNTANTA_ORGANIZACJI_LABELS = ROLA_REPREZNTANTA_ORGANIZACJI_LABELS;
}
